import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/shadcn";
import "@blocknote/shadcn/style.css";
import { AppIcon } from './ui/icon'
import { mdiHistory, mdiArrowLeft } from "@mdi/js";
import { diffLines } from "diff";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { usePageVersions } from "../hooks/usePages";
import { useNoteVersions, type NoteVersion } from "../hooks/useKnowledge";
import { VersionOrigin } from "./knowledge/VersionOrigin";
import { useTheme } from "../contexts/ThemeContext";
import type { components } from "../api/schema";
import { EmptyState } from './ui/empty-state'

type PageVersion = components["schemas"]["PageVersion"];
type TiptapDocument = components["schemas"]["TiptapDocument"];

export function formatRelativeTime(dateStr?: string | null): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = Date.now();
  const diffMs = date.getTime() - now;
  const diffSec = Math.round(diffMs / 1000);
  const abs = Math.abs(diffSec);
  try {
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    if (abs < 60) return rtf.format(diffSec, "second");
    if (abs < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
    if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), "hour");
    if (abs < 86400 * 30) return rtf.format(Math.round(diffSec / 86400), "day");
    if (abs < 86400 * 365) return rtf.format(Math.round(diffSec / (86400 * 30)), "month");
    return rtf.format(Math.round(diffSec / (86400 * 365)), "year");
  } catch {
    return date.toLocaleString();
  }
}

// Legacy fallback used only when a version has no `text_content` (pre-`PageEditor` saves).
// Lossy: only handles paragraphs and headings.
function convertTiptapToBlockNoteLegacy(tiptapDoc?: TiptapDocument | unknown) {
  const doc = tiptapDoc as { content?: unknown[] } | undefined;
  if (!doc?.content || !Array.isArray(doc.content)) return undefined;
  try {
    const blocks = doc.content.map((node: any, index: number) => {
      if (node.type === "heading") {
        return {
          id: `block-${index}`,
          type: "heading",
          props: { level: node.attrs?.level || 1 },
          content: node.content?.[0]?.text || "",
        };
      }
      return {
        id: `block-${index}`,
        type: "paragraph",
        content: node.content?.[0]?.text || "",
      };
    });
    return blocks.length > 0 ? blocks : undefined;
  } catch {
    return undefined;
  }
}

function ReadOnlyVersionView({ version }: { version: PageVersion }) {
  const { actualTheme } = useTheme();
  const editor = useCreateBlockNote();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        if (version.text_content && version.text_content.trim().length > 0) {
          // Faithful path: round-trip the lossless markdown stored on save.
          const blocks = await editor.tryParseMarkdownToBlocks(version.text_content);
          if (!cancelled && blocks && blocks.length > 0) {
            editor.replaceBlocks(editor.document, blocks);
            return;
          }
        }
        // Legacy fallback for versions saved before text_content existed.
        const legacy = convertTiptapToBlockNoteLegacy(version.content);
        if (!cancelled && legacy && legacy.length > 0) {
          editor.replaceBlocks(editor.document, legacy as any);
        }
      } catch {
        // Swallow — leave the editor empty rather than crashing the panel.
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [editor, version]);

  return (
    <div className="prose prose-lg dark:prose-invert max-w-none" data-theme={actualTheme}>
      <BlockNoteView
        editor={editor}
        editable={false}
        theme={["dark", "midnight", "halloween"].includes(actualTheme) ? "dark" : "light"}
      />
    </div>
  );
}

type DiffRowKind = "added" | "removed" | "unchanged";

interface DiffRow {
  kind: DiffRowKind;
  text: string;
}

function computeDiffRows(before: string, after: string): DiffRow[] {
  const parts = diffLines(before, after);
  const rows: DiffRow[] = [];
  for (const part of parts) {
    const kind: DiffRowKind = part.added ? "added" : part.removed ? "removed" : "unchanged";
    // Split on newlines so each visual row maps to one source line.
    const lines = part.value.split("\n");
    // diffLines includes a trailing newline on each chunk; drop the empty tail.
    if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
    for (const line of lines) {
      rows.push({ kind, text: line });
    }
  }
  return rows;
}

interface DiffNeighbor {
  text_content?: string | null;
  content?: TiptapDocument | null;
}

function PageVersionDiffView({
  current,
  newer,
}: {
  current: PageVersion;
  newer: DiffNeighbor | null;
}) {
  const { t } = useTranslation("assets");

  const rows = useMemo(() => {
    if (!newer) return null;
    // `current` is a pre-save snapshot — it represents the state BEFORE the save that
    // ended its lifetime. `newer` is the state that REPLACED it (either the next version
    // snapshot or the live page). So the diff "current → newer" answers: "what changed
    // when this version was overwritten".
    const before = current.text_content ?? "";
    const after = newer.text_content ?? "";
    return computeDiffRows(before, after);
  }, [current, newer]);

  // No newer neighbor → this is the most recent snapshot AND the live page is unavailable.
  // Show the read-only render with a caption.
  if (!newer || rows === null) {
    return (
      <div>
        <div
          className="text-xs text-muted-foreground mb-2 italic"
          data-testid="diff-first-version"
        >
          {t("page.version_diff_first_version")}
        </div>
        <div className="border border-border rounded-md p-4 bg-background">
          <ReadOnlyVersionView version={current} />
        </div>
      </div>
    );
  }

  const hasChanges = rows.some((r) => r.kind !== "unchanged");

  // Same plain text but different content JSON → formatting-only edit.
  if (!hasChanges) {
    const sameJson =
      JSON.stringify(newer.content ?? {}) === JSON.stringify(current.content ?? {});
    return (
      <div>
        <div
          className="text-xs text-muted-foreground mb-2 italic"
          data-testid={sameJson ? "diff-no-changes" : "diff-formatting-only"}
        >
          {t(sameJson ? "page.version_diff_no_changes" : "page.version_diff_formatting_only")}
        </div>
        <div className="border border-border rounded-md p-4 bg-background">
          <ReadOnlyVersionView version={current} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="border border-border rounded-md bg-background overflow-hidden"
      data-testid="page-version-diff"
    >
      {rows.map((row, i) => {
        const cls =
          row.kind === "added"
            ? "border-l-green-500 bg-green-500/10"
            : row.kind === "removed"
              ? "border-l-red-500 bg-red-500/10"
              : "border-l-transparent";
        return (
          <div
            key={i}
            className={`flex font-mono text-xs leading-relaxed border-l-4 ${cls}`}
            data-testid={`diff-line-${row.kind}`}
          >
            <span className="px-2 whitespace-pre-wrap break-words flex-1">
              {row.text || "\u00A0"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

interface PageVersionHistoryProps {
  assetUid: string;
  enabled: boolean;
  /**
   * The live page (current state). Used as the "newer neighbor" when diffing the most
   * recent snapshot — versions are pre-save snapshots, so the most recent one's "what
   * changed" is against the live page rather than another version row.
   */
  livePage?: DiffNeighbor | null;
  /** A knowledge note's history instead of the asset's single page (spec 2026-10-10-06). */
  note?: { org: string; noteUid: string };
  /** data-testid of the trigger button. */
  testId?: string;
}

export function PageVersionHistoryButton({
  assetUid,
  enabled,
  livePage,
  note,
  testId = "page-versions-btn",
}: PageVersionHistoryProps) {
  const { t } = useTranslation("assets");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<PageVersion | null>(null);

  const pageQuery = usePageVersions(assetUid, { enabled: enabled && open && !note });
  const noteQuery = useNoteVersions(note?.org ?? "", assetUid, note?.noteUid ?? "", enabled && open && !!note);
  const isLoading = note ? noteQuery.isLoading : pageQuery.isLoading;
  const pageVersions = pageQuery.data?.versions;
  const noteVersions = noteQuery.data;
  const versions = useMemo(
    () => (note ? noteVersions : pageVersions) ?? [],
    [note, noteVersions, pageVersions],
  );

  // List is DESC by created_at. The "newer neighbor" of versions[i] is versions[i - 1];
  // for the most recent snapshot (i = 0) it's the live page.
  const newer = useMemo<DiffNeighbor | null>(() => {
    if (!selected) return null;
    const idx = versions.findIndex((v) => v.uid === selected.uid);
    if (idx < 0) return null;
    if (idx === 0) return livePage ?? null;
    return versions[idx - 1] ?? null;
  }, [selected, versions, livePage]);

  if (!enabled) return null;

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSelected(null);
      }}
    >
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid={testId}
          title={t("page.version_history")}
          aria-label={t("page.version_history")}
        >
          <AppIcon path={mdiHistory} size="md" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t("page.version_history")}</SheetTitle>
        </SheetHeader>
        <div className="px-4 pb-6">
          {selected ? (
            <div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="flex items-center gap-1 text-sm text-primary hover:underline mb-4"
                data-testid="page-version-back"
              >
                <AppIcon path={mdiArrowLeft} size="sm" />
                {t("page.version_back")}
              </button>
              <div className="text-xs text-muted-foreground mb-2">
                {t("page.version_by", { name: selected.created_by?.name || "?" })}{" "}
                <span title={new Date(selected.created_at).toLocaleString()}>
                  {formatRelativeTime(selected.created_at)}
                </span>
              </div>
              <PageVersionDiffView current={selected} newer={newer} />
            </div>
          ) : isLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">...</div>
          ) : versions.length === 0 ? (
            <EmptyState variant="inline" title={t('page.version_empty')} />
          ) : (
            <ul className="divide-y divide-border">
              {versions.map((v) => (
                <li
                  key={v.uid}
                  className="py-3 flex items-center justify-between gap-3"
                  data-testid="page-version-item"
                >
                  <div className="text-sm">
                    <div className="text-foreground">
                      {t("page.version_by", { name: v.created_by?.name || "?" })}
                    </div>
                    <div
                      className="text-xs text-muted-foreground"
                      title={new Date(v.created_at).toLocaleString()}
                    >
                      {formatRelativeTime(v.created_at)}
                    </div>
                    {note && (v as NoteVersion).origin && (
                      <VersionOrigin org={note.org} origin={(v as NoteVersion).origin!} />
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelected(v)}
                    data-testid="page-version-view-btn"
                  >
                    {t("page.version_view")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
